import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { match } from 'ts-pattern';

import { ProjectService, SurveyService } from '@ps/survey-data-access';
import {
  ProjectUpdatePayloadInput,
  SURVEY_CLASSIFICATION_KIND,
  StandardSurveyPayloadInput,
  Survey,
} from '@ps/survey-utils';

/**
 * A standard survey — and a Traditional MC
 * locale, which is one with its own id — is written a row at a time, while a
 * Group MC writes every locale together against the project.
 */
export type SurveyUpdateRequest =
  | {
      readonly kind: typeof SURVEY_CLASSIFICATION_KIND.STANDARD;
      readonly surveyId: number;
      readonly input: StandardSurveyPayloadInput;
    }
  | {
      readonly kind: typeof SURVEY_CLASSIFICATION_KIND.GROUP_MC;
      readonly projectId: number;
      readonly input: ProjectUpdatePayloadInput;
    };

/**
 * The creation flow's writes, each routed to the endpoint that owns it, so
 * `SurveyCreationSaveEffects` keeps a single write collaborator.
 */
@Injectable({ providedIn: 'root' })
export class SurveyPersistenceService {
  private readonly _surveyService = inject(SurveyService);
  private readonly _projectService = inject(ProjectService);

  public update(request: SurveyUpdateRequest): Observable<Survey> {
    return match(request)
      .with(
        { kind: SURVEY_CLASSIFICATION_KIND.STANDARD },
        ({ surveyId, input }) =>
          this._surveyService.updateStandardSurvey(surveyId, input),
      )
      .with(
        { kind: SURVEY_CLASSIFICATION_KIND.GROUP_MC },
        ({ projectId, input }) =>
          this._projectService.updateMcGroupedProject(projectId, input),
      )
      .exhaustive();
  }

  public clone(
    surveyId: number,
    isServiceOperatorCreation: boolean,
  ): Observable<Survey> {
    return this._surveyService.cloneStandardSurvey(
      surveyId,
      isServiceOperatorCreation,
    );
  }
}
